import { useEffect, useState } from "react";
import { api, fileToDataUrl, formatApiError, formatPrice } from "../lib/api";
import { Plus, Edit2, Trash2, X, Save, Image as ImageIcon, Upload, ChevronDown, ChevronUp } from "lucide-react";
import ProductSearchBox from "../components/ProductSearchBox";
import RichTextEditor from "../components/RichTextEditor";
import { toast } from "sonner";

const empty = {
    name: "", description: "", price: 0, stock: 0,
    category_id: "", images: [], specifications: {},
    sku: "", brand: "", featured: false,
};

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null); // null = closed; {} = new; {...} = edit
    const [q, setQ] = useState("");
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importMode, setImportMode] = useState("replace");
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const load = () =>
        api.get("/products", { params: { limit: 200, q: q || undefined } })
            .then((r) => setItems(r.data.items || []));

    useEffect(() => {
        load();
        api.get("/categories").then((r) => setCats(r.data || []));
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [q]);

    const onDelete = async (p) => {
        if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Producto eliminado");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const onSaved = () => {
        setEditing(null);
        load();
    };

    const onImport = async () => {
        if (!importFile) {
            toast.error("Selecciona un archivo productos.json");
            return;
        }
        if (importMode === "replace" && !window.confirm(
            "¿Reemplazar todo el catálogo? Se borrarán los productos actuales y se cargará el archivo."
        )) {
            return;
        }
        setImporting(true);
        setImportResult(null);
        try {
            const form = new FormData();
            form.append("file", importFile);
            form.append("mode", importMode);
            // No fijar Content-Type: axios debe añadir el boundary de multipart/form-data
            const { data } = await api.post("/products/import/delfino", form);
            setImportResult(data);
            toast.success(`Importados ${data.products_prepared} productos`);
            setImportFile(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="p-6 md:p-10" data-testid="admin-products-page">
            <div className="border-b-2 border-[var(--lz-navy)] pb-4 mb-6 space-y-4">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="lz-section-label">Catálogo</span>
                        <h1 className="font-display text-4xl font-bold uppercase">Productos</h1>
                    </div>
                    <button onClick={() => setEditing({ ...empty })} className="lz-btn lz-btn-primary shrink-0" data-testid="new-product-btn">
                        <Plus className="w-4 h-4" /> Nuevo
                    </button>
                </div>
                <div className="w-full max-w-4xl">
                    <ProductSearchBox
                        variant="admin"
                        showStock
                        limit={30}
                        value={q}
                        onQueryChange={setQ}
                        onSelect={(p) => setEditing(p)}
                        placeholder="Buscar por nombre, código o marca…"
                        inputTestId="admin-products-search"
                        className="w-full"
                    />
                </div>
            </div>

            <div className="border-2 border-[var(--lz-navy)] bg-white mb-6" data-testid="delfino-import-panel">
                <button
                    type="button"
                    onClick={() => setImportOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#f3f4f6]"
                >
                    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--lz-orange)]">
                        <Upload className="w-4 h-4" /> Importar desde ERP (productos.json)
                    </span>
                    {importOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {importOpen && (
                    <div className="px-4 pb-4 border-t border-[var(--lz-navy)]/15 space-y-4">
                        <p className="text-sm text-[var(--lz-navy)]/70">
                            Sube el export de DELFINO (campos: CODIGO, NOMBRE, PRECIO 1, EXISTENCIA, UNIDAD).
                            No hace falta copiar el archivo al servidor: eliges el .json desde tu PC.
                        </p>
                        <div className="flex flex-wrap gap-4 items-end">
                            <label className="block text-xs font-bold uppercase tracking-widest">
                                Archivo JSON
                                <input
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={(e) => {
                                        setImportFile(e.target.files?.[0] || null);
                                        setImportResult(null);
                                    }}
                                    className="block mt-1 text-sm w-full max-w-md"
                                    data-testid="delfino-import-file"
                                />
                            </label>
                            <fieldset className="text-xs space-y-1">
                                <legend className="font-bold uppercase tracking-widest mb-1">Modo</legend>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="importMode"
                                        checked={importMode === "replace"}
                                        onChange={() => setImportMode("replace")}
                                    />
                                    Reemplazar catálogo completo
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="importMode"
                                        checked={importMode === "update"}
                                        onChange={() => setImportMode("update")}
                                    />
                                    Actualizar por SKU (sin borrar otros)
                                </label>
                            </fieldset>
                            <button
                                type="button"
                                onClick={onImport}
                                disabled={importing || !importFile}
                                className="lz-btn lz-btn-primary"
                                data-testid="delfino-import-submit"
                            >
                                {importing ? "Importando…" : "Importar"}
                            </button>
                        </div>
                        {importResult && (
                            <div className="text-sm font-mono bg-[#f3f4f6] p-3 border border-[var(--lz-navy)]/20 space-y-1" data-testid="delfino-import-result">
                                <div>Registros en archivo: {importResult.rows_in_file}</div>
                                <div>Productos importados: {importResult.products_prepared}</div>
                                <div>Total en tienda: {importResult.total_in_db}</div>
                                {importResult.mode === "replace" ? (
                                    <div>Eliminados: {importResult.deleted} · Insertados: {importResult.inserted}</div>
                                ) : (
                                    <div>Actualizados: {importResult.updated} · Nuevos: {importResult.inserted}</div>
                                )}
                                {importResult.warnings?.length > 0 && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer font-bold text-[var(--lz-orange)]">
                                            {importResult.warnings.length} advertencias
                                        </summary>
                                        <ul className="mt-1 max-h-32 overflow-auto text-xs list-disc pl-4">
                                            {importResult.warnings.map((w) => (
                                                <li key={w}>{w}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="border-2 border-[var(--lz-navy)] bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[var(--lz-navy)] text-white text-[10px] uppercase tracking-widest">
                            <th className="p-3 text-left">Img</th>
                            <th className="p-3 text-left">Nombre</th>
                            <th className="p-3 text-left">Categoría</th>
                            <th className="p-3 text-left">SKU</th>
                            <th className="p-3">Precio</th>
                            <th className="p-3">Stock</th>
                            <th className="p-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#0f1419]/15">
                        {items.map((p) => (
                            <tr key={p.id} data-testid={`admin-prod-row-${p.id}`} className="hover:bg-[#f3f4f6]">
                                <td className="p-3">
                                    <div className="w-12 h-12 bg-[#f3f4f6] border border-[var(--lz-navy)] overflow-hidden">
                                        {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 m-3 text-[var(--lz-navy)]/30" />}
                                    </div>
                                </td>
                                <td className="p-3 font-bold">{p.name}{p.featured && <span className="ml-2 lz-tag bg-[var(--lz-orange)]">⭐</span>}</td>
                                <td className="p-3 text-xs">{cats.find((c) => c.id === p.category_id)?.name || "—"}</td>
                                <td className="p-3 font-mono text-xs">{p.sku || "—"}</td>
                                <td className="p-3 font-mono">{formatPrice(p.price)}</td>
                                <td className="p-3 font-mono">{p.stock}</td>
                                <td className="p-3">
                                    <div className="flex gap-1">
                                        <button onClick={() => setEditing(p)} className="p-2 border border-[var(--lz-navy)] hover:bg-[var(--lz-orange)]" data-testid={`edit-prod-${p.id}`}><Edit2 className="w-3 h-3" /></button>
                                        <button onClick={() => onDelete(p)} className="p-2 border border-[var(--lz-navy)] hover:bg-red-600 hover:text-white" data-testid={`delete-prod-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="p-10 text-center text-[var(--lz-navy)]/60">Sin productos</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {editing && (
                <ProductDialog initial={editing} cats={cats} onClose={() => setEditing(null)} onSaved={onSaved} />
            )}
        </div>
    );
}

function ProductDialog({ initial, cats, onClose, onSaved }) {
    const isNew = !initial.id;
    const [form, setForm] = useState({
        name: initial.name || "",
        description: initial.description || "",
        price: initial.price || 0,
        stock: initial.stock || 0,
        category_id: initial.category_id || "",
        images: initial.images || [],
        specifications: initial.specifications || {},
        sku: initial.sku || "",
        brand: initial.brand || "",
        featured: !!initial.featured,
    });
    const [busy, setBusy] = useState(false);
    const [specKey, setSpecKey] = useState("");
    const [specVal, setSpecVal] = useState("");

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        const urls = await Promise.all(files.map(fileToDataUrl));
        setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
        e.target.value = "";
    };

    const onAddImageUrl = () => {
        const url = window.prompt("URL de imagen");
        if (url) setForm((f) => ({ ...f, images: [...f.images, url] }));
    };

    const removeImg = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

    const addSpec = () => {
        if (!specKey.trim()) return;
        setForm((f) => ({ ...f, specifications: { ...f.specifications, [specKey]: specVal } }));
        setSpecKey(""); setSpecVal("");
    };

    const removeSpec = (k) => setForm((f) => {
        const next = { ...f.specifications };
        delete next[k];
        return { ...f, specifications: next };
    });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                stock: Number(form.stock),
                category_id: form.category_id || null,
            };
            if (isNew) {
                await api.post("/products", payload);
                toast.success("Producto creado");
            } else {
                await api.put(`/products/${initial.id}`, payload);
                toast.success("Producto actualizado");
            }
            onSaved();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4" data-testid="product-dialog">
            <div className="bg-white border-2 border-[var(--lz-navy)] w-full max-w-3xl my-8">
                <div className="flex items-center justify-between bg-[var(--lz-navy)] text-white px-5 py-3">
                    <h2 className="font-display font-bold uppercase">{isNew ? "Nuevo Producto" : "Editar Producto"}</h2>
                    <button onClick={onClose} className="p-1 hover:text-[var(--lz-orange)]" data-testid="prod-dialog-close"><X className="w-5 h-5" /></button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Nombre *</label>
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lz-input" data-testid="prod-name" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">SKU</label>
                            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="lz-input" data-testid="prod-sku" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Marca</label>
                            <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="lz-input" data-testid="prod-brand" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Categoría</label>
                            <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="lz-input" data-testid="prod-category">
                                <option value="">Sin categoría</option>
                                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Precio (MXN) *</label>
                            <input type="number" step="0.01" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="lz-input" data-testid="prod-price" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Stock *</label>
                            <input type="number" min="0" required value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="lz-input" data-testid="prod-stock" />
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="prod-featured" />
                        <span className="font-bold uppercase text-xs tracking-widest">Producto destacado</span>
                    </label>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Descripción</label>
                        <RichTextEditor value={form.description} onChange={(html) => setForm((f) => ({ ...f, description: html }))} />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-bold uppercase tracking-widest">Imágenes ({form.images.length})</label>
                            <div className="flex gap-1">
                                <label className="lz-btn lz-btn-outline !py-1.5 !px-3 cursor-pointer text-xs" data-testid="prod-upload-files">
                                    <Plus className="w-3 h-3" /> Subir
                                    <input type="file" multiple accept="image/*" onChange={onFiles} className="hidden" />
                                </label>
                                <button type="button" onClick={onAddImageUrl} className="lz-btn lz-btn-ghost !py-1.5 !px-3 text-xs" data-testid="prod-add-url">URL</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {form.images.map((img, i) => (
                                <div className="img-preview" key={`${img.slice(-32)}-${i}`} data-testid={`prod-img-${i}`}>
                                    <img src={img} alt="" />
                                    <button type="button" onClick={() => removeImg(i)} aria-label="Quitar"><X className="w-3 h-3 mx-auto" /></button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest mb-1 block">Especificaciones técnicas</label>
                        <div className="flex gap-2 mb-2">
                            <input value={specKey} onChange={(e) => setSpecKey(e.target.value)} placeholder="Atributo" className="lz-input flex-1" data-testid="spec-key" />
                            <input value={specVal} onChange={(e) => setSpecVal(e.target.value)} placeholder="Valor" className="lz-input flex-1" data-testid="spec-val" />
                            <button type="button" onClick={addSpec} className="lz-btn lz-btn-outline" data-testid="spec-add">Añadir</button>
                        </div>
                        {Object.keys(form.specifications).length > 0 && (
                            <table className="w-full text-sm border-2 border-[var(--lz-navy)]">
                                <tbody>
                                    {Object.entries(form.specifications).map(([k, v]) => (
                                        <tr key={k} className="border-t border-[var(--lz-navy)]/15">
                                            <td className="p-2 font-bold">{k}</td>
                                            <td className="p-2">{String(v)}</td>
                                            <td className="p-2 w-10"><button type="button" onClick={() => removeSpec(k)} className="text-red-600"><X className="w-3 h-3" /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="flex gap-2 justify-end pt-2 border-t-2 border-[var(--lz-navy)]">
                        <button type="button" onClick={onClose} className="lz-btn lz-btn-ghost">Cancelar</button>
                        <button type="submit" disabled={busy} className="lz-btn lz-btn-primary" data-testid="prod-save">
                            <Save className="w-4 h-4" /> {busy ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
